import { captureException } from '@mentimeter/errors/sentry';
import { MentiError } from '@mentimeter/error-utils/error';
import type { Question } from '@mentimeter/http-clients';
import type {
  Slide as VotingSlide,
  InteractiveContent as VotingInteractiveContent,
  StaticContentWithVisualization,
} from '@mentimeter/voting-schema/api-types-overrides';
import {
  DEFAULT_RESPONSE_RANGE,
  MULTIPLE_VOTE_POLICIES,
  SCORING_TIME_BASED_TYPE,
} from '../../../../constants';
import { responseModeToQuestionActive } from '../../../../backward-compatibility/converters/interactive-content';
import { visualizationToQuestionSubType } from '../../../../backward-compatibility/converters/interactive-content/visualization-to-question-subtype';
import { getCachedChoicesProperty } from '../converters/slide/get-cached-choices-property';
import { captureNoInteractiveContentError } from '../../../common/capture-no-interactive-content-error';

const INTERACTIVE_QUESTION_PROPERTIES = [
  'question',
  'question_description',
  'multiple_votes',
  'range',
  'choices',
  'steps',
  'sub_type',
  'active',
  'time_based_scoring',
  'countdown_to_vote',
  'max_votes',
] as const satisfies Array<keyof Question>;

export type InteractiveQuestionProperty =
  (typeof INTERACTIVE_QUESTION_PROPERTIES)[number];

export const getInteractiveQuestionProperty = (
  slide: VotingSlide,
  prop: InteractiveQuestionProperty,
) => {
  const interactiveContent = slide.interactiveContents?.[0];

  if (!interactiveContent) {
    return captureNoInteractiveContentError(slide, prop);
  }

  switch (prop) {
    case 'question':
      return interactiveContent.title;
    case 'question_description':
      return interactiveContent.description;
    case 'multiple_votes': {
      return isMultipleVotes(interactiveContent);
    }
    case 'max_votes': {
      return interactiveContent.maxEntries ?? 1;
    }
    case 'choices': {
      return getCachedChoicesProperty(slide, interactiveContent.choices);
    }
    case 'steps': {
      return interactiveContent.choices.length;
    }
    case 'range': {
      /*
       * The `Question.range` type is non-nullable, while its replacement `Slide.interactiveContents[].responseRange` is.
       * We provide this fallback value so that type-safe accesses to the `range` property on a migrated object will not
       * crash at runtime.
       */
      return interactiveContent.responseRange ?? DEFAULT_RESPONSE_RANGE;
    }
    case 'sub_type': {
      return getQuestionSubType(
        (slide.staticContent as StaticContentWithVisualization)
          .interactiveContentStyling,
      );
    }
    case 'time_based_scoring': {
      return interactiveContent.scoring === SCORING_TIME_BASED_TYPE;
    }
    case 'countdown_to_vote': {
      return interactiveContent.countdown;
    }
    case 'active': {
      return responseModeToQuestionActive(interactiveContent.responseMode);
    }
    default:
      const error = new MentiError(
        `Unhandled interactive question property: \`${String(prop)}\``,
        {
          feature: 'compatibility-layer',
        },
      );
      captureException(error);

      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn(error.message);
      }
  }
};

export const isInteractiveQuestionProperty = (
  prop: keyof Question,
): prop is InteractiveQuestionProperty => {
  return INTERACTIVE_QUESTION_PROPERTIES.includes(
    prop as InteractiveQuestionProperty,
  );
};

const getQuestionSubType = (
  interactiveContentStyling: StaticContentWithVisualization['interactiveContentStyling'],
) => {
  if (interactiveContentStyling?.visualization) {
    return visualizationToQuestionSubType(
      interactiveContentStyling?.visualization,
    );
  }

  return null;
};

const isMultipleVotes = (interactiveContent: VotingInteractiveContent) => {
  return MULTIPLE_VOTE_POLICIES.includes(interactiveContent.responsePolicy);
};
